import { SchoolAndDaycareFacilityTypesDTO, SchoolAndDaycareProperty } from '@cast/leadcast-types';
import { UpsertSchoolAndDaycarePropertiesRequestBody } from '@cast/leadcast-types/src/sampling/requests';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { plainToInstance } from 'class-transformer';
import { MRT_PaginationState } from 'material-react-table';
import qs from 'qs';
import { SamplingInstructions } from 'types/models/admin';
import {
  PRIVATE_API_SampleOrderStatus,
  PRIVATE_API_SamplingKit,
  SampleData,
  SampleOrder,
  SamplePoolEntry,
  SampleProperty,
  SamplingLocation,
  SamplingProgram,
  SamplingProgramTypes,
  SamplingPurchaseOrder,
  SiteTypes,
  TapSampleForm,
  TapSampleFormSettings,
} from 'types/models/samples';
import { SamplingPropertyV2 } from 'types/models/samples/SamplePropertyV2';
import { SampleResult } from 'types/models/samples/SampleResult';
import { PaginatedResponse } from 'types/PaginatedResponse';
import AddSamplingProgram from 'types/requests/sampling/AddSamplingProgram';
import { OrderWaterTestRequest } from 'types/requests/sampling/OrderWaterTestRequest';
import UpdateSamplingProgram from 'types/requests/sampling/UpdateSamplingProgram';
import { ORDER_STATUSES } from 'utils/constants';

type SampleOrderStatuses = (typeof ORDER_STATUSES)[keyof typeof ORDER_STATUSES];

export class SamplesService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getSampleProperties(): Promise<SampleProperty[]> {
    const { data } = await this.httpClient.get<SampleProperty[]>(`/Properties`);
    return data;
  }

  async getProgramTypes(): Promise<SamplingProgramTypes[]> {
    const { data } = await this.httpClient.get<SamplingProgramTypes[]>(`/SamplingPrograms/Types`);
    return data;
  }

  async getSamplingSiteTypes(): Promise<SiteTypes[]> {
    const { data } = await this.httpClient.get<SiteTypes[]>(`/SamplingPrograms/SiteTypes`);
    return data;
  }
  async getPurchaseOrders(): Promise<SamplingPurchaseOrder[]> {
    const { data } = await this.httpClient.get<SamplingPurchaseOrder[]>(
      `/SamplingPrograms/PurchaseOrders`
    );
    return data;
  }

  async getSamplingKits(): Promise<PRIVATE_API_SamplingKit[]> {
    const { data } =
      await this.httpClient.get<PRIVATE_API_SamplingKit[]>(`/Dropdowns/SamplingKits`);
    return data;
  }

  async addSamplingInstructions(
    file: File,
    filename: string,
    bucketLocation: string
  ): Promise<SamplingInstructions> {
    const form = new FormData();
    form.append('files', file);
    const { data } = await this.httpClient.post<SamplingInstructions>(
      `/SamplingInstructions?filename=${filename}${
        bucketLocation ? `&bucketLocation=${bucketLocation}` : ''
      }`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  }

  async getSamplingPrograms(): Promise<SamplingProgram[]> {
    const { data } = await this.httpClient.get<SamplingProgram[]>(`/SamplingPrograms`);
    return data;
  }

  async updateSamplingProgram(toUpdate: UpdateSamplingProgram, id: string): Promise<void> {
    await this.httpClient.put<void>(`/SamplingPrograms/${id}`, toUpdate);
  }

  async deleteSamplingProgram(id: string): Promise<void> {
    await this.httpClient.delete(`/SamplingPrograms/${id}`);
  }

  //This is returning an id, which seems unnecessary given that it's not being used. It also makes this type incorrect.
  async addSamplingProgram(toAdd: AddSamplingProgram): Promise<void> {
    await this.httpClient.post<void>(`/SamplingPrograms`, toAdd);
  }

  async getOrderStatuses(): Promise<PRIVATE_API_SampleOrderStatus[]> {
    const { data } =
      await this.httpClient.get<PRIVATE_API_SampleOrderStatus[]>(`/Dropdowns/OrderStatuses`);
    return data;
  }

  async getSamplesData(): Promise<SampleData[]> {
    const { data } = await this.httpClient.get<SampleData[]>(`/Samples`);
    return data;
  }

  async getSampleOrderById(id: string): Promise<SampleOrder> {
    const { data } = await this.httpClient.get<SampleOrder>(`/SampleOrders/${id}`);
    return plainToInstance(SampleOrder, data);
  }

  async getSampleOrders(): Promise<SampleOrder[]> {
    const { data } = await this.httpClient.get<SampleOrder[]>(`/SampleOrders`);
    return data.map((so) => plainToInstance(SampleOrder, so));
  }

  async addSampleOrder(order: OrderWaterTestRequest): Promise<SampleOrder> {
    const { data } = await this.httpClient.post<SampleOrder>(`/SampleOrders`, order);
    return plainToInstance(SampleOrder, data);
  }

  async requestSampleOrder(
    order: Omit<OrderWaterTestRequest, 'samplingProgramId' | 'samplingKitId'>
  ): Promise<SampleOrder> {
    const { data } = await this.httpClient.post<SampleOrder>(`/SampleOrders/Request`, order);
    return plainToInstance(SampleOrder, data);
  }

  async moveSampleOrderToStatus(
    id: string,
    status: SampleOrderStatuses,
    comment: string
  ): Promise<SampleOrder> {
    const { data } = await this.httpClient.post<SampleOrder>(
      `/SampleOrders/${id}/Status/${status}`,
      {
        comment: comment,
      }
    );
    return plainToInstance(SampleOrder, data);
  }

  async approveSampleOrder({
    comment,
    id,
    samplingProgramId,
    samplingKitId,
  }: {
    id: string;
    comment: string;
    samplingProgramId: string;
    samplingKitId: string;
  }): Promise<SampleOrder> {
    const { data } = await this.httpClient.post<SampleOrder>(`/SampleOrders/${id}/Approve`, {
      comment,
      samplingProgramId,
      samplingKitId,
    });
    return plainToInstance(SampleOrder, data);
  }

  async userRequestSampleOrder(request: OrderWaterTestRequest): Promise<SampleOrder> {
    const { data } = await this.httpClient.post<SampleOrder>(`/PublicUser/SampleOrder`, request);
    return plainToInstance(SampleOrder, data);
  }

  async getWaterSampleConfiguration(): Promise<TapSampleFormSettings> {
    const { data } = await this.httpClient.get<TapSampleFormSettings>(`/TapSampleFormSettings`);
    return data;
  }

  async getWaterSampleLocations(): Promise<SamplingLocation[]> {
    const { data } = await this.httpClient.get<SamplingLocation[]>(
      `/TapSampleForms/SampleLocations`
    );
    return data;
  }

  async getSamplePoolTableData(): Promise<SamplePoolEntry[]> {
    const { data } = await this.httpClient.get<SamplePoolEntry[]>(`/SamplePool`);
    return data;
  }

  async updateDWSF(id: string, updatedForm: Partial<TapSampleForm>): Promise<TapSampleForm> {
    const { data } = await this.httpClient.put<TapSampleForm>(`/TapSampleForm/${id}`, updatedForm, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return data;
  }

  async approveDWSF(id, comment): Promise<TapSampleForm> {
    const { data } = await this.httpClient.put<TapSampleForm>(`/TapSampleForm/${id}/Approve`, {
      comment,
    });
    return data;
  }

  async rejectDWSF(id, comment): Promise<TapSampleForm> {
    const { data } = await this.httpClient.put<TapSampleForm>(`/TapSampleForm/${id}/Reject`, {
      comment,
    });
    return data;
  }

  async uploadSamplingPoolFromTemplate(formData): Promise<void> {
    const { data } = await this.httpClient.post<void>(`/SamplePool`, formData, {
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        Accept: 'application/octet-stream',
      },
    });
    return data;
  }

  async getSamplingPropertyById(propertyId: string): Promise<SamplingPropertyV2> {
    const resp = await this.publicHttpClient.get<SamplingPropertyV2>(
      `/sampling/properties/${propertyId}`,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(SamplingPropertyV2, resp.data);
  }

  async getSamplingProperties(searchCriteria): Promise<PaginatedResponse<SamplingPropertyV2>> {
    const sort = searchCriteria.sorting[0] ?? { id: null, desc: true };
    const searchStr = qs.stringify({
      page: searchCriteria.pageIndex,
      number: searchCriteria.pageSize,
      sort: sort.id,
      sortDir: sort.desc ? 'desc' : 'asc',
      ...searchCriteria.filters,
    });

    const resp = await this.publicHttpClient.get<SamplingPropertyV2[]>(
      `/sampling/properties?${searchStr}`,
      {
        paramsSerializer: { indexes: null },
        withCredentials: false,
      }
    );

    if (resp.status === 404) {
      return {
        data: [],
        pagination: {
          prev: null,
          next: null,
          last: null,
          count: 0,
          limit: null,
        },
      };
    }
    resp.data = resp.data.map((sp) => plainToInstance(SamplingPropertyV2, sp));
    return resp;
  }

  async getSampleResults(): Promise<SampleResult[]> {
    const resp = await this.publicHttpClient.get<SampleResult[]>(`/sampling/results`, {
      paramsSerializer: { indexes: null },
      withCredentials: false,
    });
    return resp.data?.map((sr) => plainToInstance(SampleResult, sr)) ?? [];
  }

  async getSchoolAndDaycareProperties(searchCriteria: {
    pagination: MRT_PaginationState;
    sort?: any;
    filters: any;
  }): Promise<PaginatedResponse<SchoolAndDaycareProperty>> {
    const formattedFilters = Object.entries(searchCriteria.filters).reduce(
      (acc, [key, filterValue]) => {
        if (typeof filterValue === 'object' && filterValue !== null && 'value' in filterValue) {
          acc[`filter[${key}]`] = filterValue.value;
        } else {
          acc[`filter[${key}]`] = filterValue;
        }
        return acc;
      },
      {}
    );

    let params: any = {
      'page[number]': searchCriteria.pagination.pageIndex + 1,
      'page[size]': searchCriteria.pagination.pageSize,
      sort: searchCriteria?.sort
        ? {
            field: searchCriteria?.sort?.id,
            direction: searchCriteria?.sort?.desc ? 'desc' : 'asc',
          }
        : null,
      ...formattedFilters,
    };

    const resp = await this.publicHttpClient.get<SchoolAndDaycareProperty[]>(
      `/sampling/schoolAndDaycare/properties`,
      {
        params,
        paramsSerializer: { indexes: null },
        withCredentials: false,
      }
    );

    return resp;
  }

  async upsertSchoolAndDaycareProperties(body: UpsertSchoolAndDaycarePropertiesRequestBody) {
    const resp = await this.publicHttpClient.patch(`/sampling/schoolAndDaycare/properties`, body, {
      withCredentials: false,
    });
    return resp;
  }

  async getFacilityTypes(): Promise<SchoolAndDaycareFacilityTypesDTO[]> {
    const { data } = await this.publicHttpClient.get<SchoolAndDaycareFacilityTypesDTO[]>(
      `/sampling/schoolAndDaycare/facilityTypes`,
      {
        withCredentials: false,
      }
    );
    return data;
  }
}
